import {_} from 'lodash';
import * as ko from "knockout";
import {
    IMessageData,
    IObjectData,
    IOrganization,
    IRouteDefinitionParams,
    ISystem,
    ISystemPermission,
    ISystemRole,
    TSyncfield
} from '../../index';
import {global, lm} from "../../appContext";
import {KOSubscriptionTracker} from "../../util/KOSubscriptionTracker";
import {MAX_CONCURRENT_REQUESTS, MAX_RESULTS_PER_PAGE, ObjectTypeId} from "../../constants";
import {
    createErrorMessage,
    getPermissionDisplayName,
    getSyncFieldDisplayName,
    getSyncFieldValue
} from "../../util/misc";
import PromiseRequest from "../../req/PromiseRequest";
import {getSearchSystemParams} from "../../util/request";
import {nsRoute} from "../../util/route";
import {ICreatePermissionsParams} from "./index";
import {Organization} from "./system-permission/Organization";
import {getHash, getRouteDefinition, getContinueButton, getDiscardButton, getPreviousButton, getSubmitButton} from "../../constants/RouteDefinitions";
import {memoryStore} from "../../util/memorystore";
import {WIZARD_PERMISSIONS_PREFILL_USERS_KEY} from "./wizardPrefill";
import {REQUEST_MESSAGE, UPDATING_MESSAGE} from "../../constants/Language";
import {convertFromServerString, convertToServerString} from "../../util/datetime";
import {ButtonManager} from "../../util/ButtonManager";
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import wizardHtml from './wizard.html?raw';

export let template = wizardHtml;

enum Step {
    Users,
    Load,
    LoadResult,
    Permissions,
    Confirm,
    Execute,
    Receipt,
}

type IObjectError = { reason: string, user: IObjectData };

export class viewModel extends ParsedRouteDataDecorator {
    private readonly Step = Step;
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    private readonly isReady: KnockoutObservable<boolean | null> = ko.observable(null);
    private readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private readonly step: KnockoutObservable<Step | null> = ko.observable(null);

    private readonly selectedUsers: KnockoutObservableArray<IObjectData> = ko.observableArray<IObjectData>([]);

    private permissionCreateParams: ICreatePermissionsParams = {
        isValid: ko.observable(false),
        getPermissions: () => [],
        mutableOrganizations: ko.observableArray<Organization>([]),
    };
    private submitRequests: PromiseRequest[] = [];
    private fractionComplete: KnockoutObservable<number | null> = ko.observable(null);

    private updateSuccess: IObjectData[] = [];
    private updateError: IObjectError[] = [];
    private loadSuccess!: IObjectData[];
    private loadError!: IObjectError[];
    private nextStep: KnockoutObservable<Step | null> = ko.observable(null);
    private prevStep: KnockoutObservable<Step | null> = ko.observable(null);
    private readonly pageButtons: ButtonManager = new ButtonManager()

    constructor(params: IRouteDefinitionParams) {
        super(params);
        global.login.setSelectedOrganizationById(this.__routeParams?.['orgnr'] ?? this.__routeParams?.['orgid'] ?? '');
        this.kosub.subscribe(this.step, () => this.updateStep());

        this.pageButtons.add(getPreviousButton({
            onClick: () => this.onPrevStepClick(),
            enabled: () => this.isPrevButtonActive(),
            validator: () => this.isNavButtonAvailable(),
        }))
        this.pageButtons.add(getContinueButton({
            onClick: () => this.onNextStepClick(),
            enabled: () => this.isNextButtonActive(),
            iconPlacement: 'right',
            validator: () => this.isNavButtonAvailable() && !this.isConfirmStep(),
        }))
        this.pageButtons.add(getSubmitButton({
            onClick: () => this.onNextStepClick(),
            enabled: () => this.isNextButtonActive(),
            validator: () => this.isNavButtonAvailable() && this.isConfirmStep(),
        }))
        this.pageButtons.add(getDiscardButton({
            label: 'start over',
            onClick: () => this.onRestartClick(),
            validator: () => this.step() === Step.Receipt,
        }))

        this.init();
    }

    private init() {
        const systemid = Number(this.__routeParams?.systemid);
        if (systemid) {
            this.messageData({message: REQUEST_MESSAGE});
            global.getPromiseRequest('searchsystem')
                .request(getSearchSystemParams({
                    resultsprpage: MAX_RESULTS_PER_PAGE,
                }))
                .then((data: ISystem[]) => {
                    const system = data.find(s => s.id == systemid);
                    const roleid = Number(this.__routeParams?.roleid);
                    if (system && roleid) {
                        system.roles.find((r: ISystemRole): boolean => r.id == roleid);
                    }
                    this.initEnd();
                });
        } else {
            this.initEnd();
        }
    }

    private readonly StepNames: Record<Step, string> = {
        [Step.Users]: 'choose subject|s:((user|p|ac))|ac',
        [Step.Load]: '((loading|ac)) ((user|p|ac))|td',
        [Step.LoadResult]: `error|p|ac`,
        [Step.Permissions]: 'choose permission|ac',
        [Step.Confirm]: 'confirm|ac',
        [Step.Execute]: 'updating|td|ac',
        [Step.Receipt]: 'result|p|ac',
    }

    private readonly stepName: KnockoutComputed<string | null> = ko.pureComputed(() => {
        const step = this.step()
        if (step == null) {
            return null
        }
        const str = this.StepNames[step];
        return str ? lm.t(str) : null
    })

    private initEnd() {
        this.messageData(null);
        this.updateSuccess = [];
        this.updateError = [];
        this.fractionComplete(null);
        this.messageData(null);
        this.isReady(true);

        const prefillUsers = memoryStore.getVal(WIZARD_PERMISSIONS_PREFILL_USERS_KEY);
        if (prefillUsers?.length) {
            memoryStore.get(WIZARD_PERMISSIONS_PREFILL_USERS_KEY)(null);
            this.selectedUsers(prefillUsers);
            requestAnimationFrame(() => this.step(Step.Load));
            return;
        }

        this.selectedUsers([]);
        requestAnimationFrame(() => this.step(Step.Users));
    }

    private updateStep() {
        global.snackbarData({message: ''});
        switch (this.step()) {
            case Step.Users:
                this.nextStep(Step.Load);
                this.prevStep(null);
                break;
            case Step.Load:
                this.nextStep(null);
                this.prevStep(null);
                this.requestUsers();
                break;
            case Step.LoadResult:
                this.nextStep(Step.Permissions);
                this.prevStep(Step.Users);
                break;
            case Step.Permissions:
                this.nextStep(Step.Confirm);
                this.prevStep(Step.Users);
                break;
            case Step.Confirm:
                this.nextStep(Step.Execute);
                this.prevStep(Step.Permissions);
                break;
            case Step.Execute:
                this.exec();
                this.nextStep(null);
                this.prevStep(null);
                break;
            case Step.Receipt:
                break;
        }
    }

    private onWriteIdsDialogClick() {
        global.dialogManager.addCustom({
            name: 'component/permission/wizard/dialog/paste',
            params: {
                __dialog: {callbackFn: (data: IObjectData[]) => this.onConfirmClick(data)},
            }
        })
    }

    private isPrevButtonActive = ko.pureComputed((): boolean => {
        switch (this.step()) {
            case Step.Users:
                return false;
            case Step.LoadResult:
            case Step.Permissions:
            case Step.Confirm:
                return true;
            case Step.Execute:
            case Step.Receipt:
                return false;
            default:
                return false;
        }
    });

    private isNextButtonActive = ko.pureComputed((): boolean => {
        switch (this.step()) {
            case Step.Users:
                return !!this.selectedUsers().length;
            case Step.Permissions:
                return this.permissionCreateParams.isValid();
            case Step.LoadResult:
            case Step.Confirm:
                return true;
            default:
                return false;
        }
    });

    private readonly isConfirmStep = ko.pureComputed((): boolean => this.step() === Step.Confirm);

    private onNextStepClick() {
        this.step(this.nextStep());
    }

    private onPrevStepClick() {
        this.step(this.prevStep());
    }

    private isNavButtonAvailable(): boolean {
        if (this.step() == null) {
            return false;
        }
        switch (this.step()) {
            case Step.Execute:
            case Step.Receipt:
                return false;
            default:
                return true;
        }
    }

    private onRestartClick() {
        this.permissionCreateParams.mutableOrganizations([]);
        this.init();
    }

    private requestUsers() {
        const users = _.cloneDeep(this.selectedUsers());
        this.loadSuccess = [];
        this.loadError = [];
        return new Promise((resolve: (value: unknown) => void) => {
            this.messageData({message: REQUEST_MESSAGE});
            const doNext = () => {
                const user = users.shift();
                const p: PromiseRequest = new PromiseRequest('getobject');
                this.submitRequests.push(p);
                return p
                    .request({id: user.id, orgnr: user.orgnr, manual: false})
                    .then((data: IObjectData[]) => {
                        const object = data[0];
                        if (object) {
                            this.loadSuccess.push(object);
                        }
                        this.onExecItemComplete(users, this.loadSuccess, this.loadError, this.selectedUsers().length, REQUEST_MESSAGE, doNext, resolve);
                    })
                    .catch((reason: string) => {
                        this.loadError.push({reason, user});
                        this.onExecItemComplete(users, this.loadSuccess, this.loadError, this.selectedUsers().length, REQUEST_MESSAGE, doNext, resolve);
                    });
            };

            for (let i = 0; i < Math.min(users.length, MAX_CONCURRENT_REQUESTS); i++) {
                doNext();
            }
        })
            .then(() => {
                this.fractionComplete(null);
                this.messageData(null);
                if (this.loadError.length) {
                    this.step(Step.LoadResult);
                } else {
                    this.step(Step.Permissions)
                }
            })
            .catch(reason => {
                this.fractionComplete(null);
                this.messageData(createErrorMessage(String(reason)));
                this.step(Step.LoadResult);
            });
    }

    private exec() {
        this.messageData({message: UPDATING_MESSAGE});
        const users = _.cloneDeep(this.loadSuccess);
        return new Promise((resolve: (value: unknown) => void) => {
            const doNext = () => {
                const u = _.cloneDeep(users.shift())

                // convert any existing expirydate to a server friendly format
                // e.g. convert "5/23/2025 12:00:00 AM" to "23.05.2025 00:00"
                u.permission.forEach((o: IOrganization) => {
                    o.systempermission.forEach((sp: ISystemPermission) => {
                        if (sp.expirydate) {
                            sp.expirydate = convertToServerString(convertFromServerString(sp.expirydate)) ?? ''
                        }
                    })
                })

                const user: IObjectData = {
                    id: u.id,
                    orgnr: u.orgnr,
                    permission: u.permission,
                    syncfields: u.syncfields,
                } as IObjectData

                const p: PromiseRequest = new PromiseRequest('updatepermission');
                this.submitRequests.push(p);

                const pTrgArr = user.permission;
                const pSrcArr = this.permissionCreateParams.getPermissions();

                // add system permissions to the user's permission array
                pSrcArr.filter(pSrc => !pTrgArr.find(oTrg => oTrg.organizationid == pSrc.organizationid))
                    .forEach(pSrc => {
                        const pTrg = _.cloneDeep(global.login.getOrganizationById(pSrc.organizationid));
                        pTrg.systempermission = [];
                        pTrgArr.push(pTrg);
                    });

                pSrcArr.forEach(pSrc => {
                    const pTrg = pTrgArr.find(oTrg => oTrg.organizationid == pSrc.organizationid);
                    if (!pTrg) {
                        return
                    }
                    pSrc.systempermission.forEach(spSrc => {
                        const sp = pTrg.systempermission.find(spTrg =>
                            spTrg.system.id == spSrc.system.id &&
                            spTrg.role.id == spSrc.role.id &&
                            spTrg.managerOf == spSrc.managerOf
                        );
                        if (!sp) {
                            // create new
                            pTrg.systempermission.push(spSrc);
                        } else {
                            // update (with old id)
                            const idx = pTrg.systempermission.indexOf(sp);
                            const existing = pTrg.systempermission[idx];
                            if (!existing) {
                                return;
                            }
                            const id = existing.id || 0
                            pTrg.systempermission[idx] = _.clone(spSrc);
                            pTrg.systempermission[idx]!.id = id
                        }
                    })
                })

                const jobject = JSON.stringify(user);
                return p
                    .request({jobject: jobject})
                    .then(() => {
                        this.updateSuccess.push(user);
                        this.onExecItemComplete(users, this.updateSuccess, this.updateError, this.loadSuccess.length, UPDATING_MESSAGE, doNext, resolve);
                    })
                    .catch(reason => {
                        this.updateError.push({reason, user});
                        this.onExecItemComplete(users, this.updateSuccess, this.updateError, this.loadSuccess.length, UPDATING_MESSAGE, doNext, resolve);
                    });
            };

            for (let i = 0; i < Math.min(users.length, MAX_CONCURRENT_REQUESTS); i++) {
                doNext();
            }
        })
            .then(() => {
                global.cacheManager.removeCache('getobject');
                global.cacheManager.removeCache('searchobject');
                this.fractionComplete(null);
                this.messageData(null);
                this.step(Step.Receipt);
            })
            .catch(reason => {
                this.fractionComplete(null);
                this.messageData(createErrorMessage(String(reason)));
                this.step(Step.Receipt);
            });
    }

    private onExecItemComplete(rest: IObjectData[], success: IObjectData[], error: IObjectError[], total: number, msg: string, cbNext: () => void, cbEnd: (success: any, error: any) => void): void {
        const numComplete = success.length + error.length;
        this.fractionComplete(numComplete / total);
        const fraction = this.fractionComplete() ?? 0
        this.messageData({message: `${lm.t(msg)} ${Math.round(fraction * 100)}%`});
        if (rest.length) {
            cbNext();
        } else if (numComplete == total) {
            cbEnd(success, error);
        }
    }

    private getUserDisplayName(objectData: IObjectData): string {
        return getSyncFieldDisplayName(ObjectTypeId.User, objectData.syncfields);
    }

    private getSyncFieldValue(key: TSyncfield, obj: IObjectData) {
        return getSyncFieldValue(key, obj.syncfields);
    }

    private getUserRouteHash(obj: IObjectData): string {
        const route = getRouteDefinition('userdetails')
        return route ? (getHash(route, {id: obj.id}) ?? '') : ''
    }

    private onConfirmClick(data: IObjectData[]) {
        const oldLength = this.selectedUsers().length
        const newDataList = _.uniqBy(this.selectedUsers().concat(data), (o: IObjectData) => o.id)
        const newLength = newDataList.length
        const numNew = newLength - oldLength
        if (numNew > 0) {
            this.selectedUsers(newDataList)
            const message = `added subject|s:((user|n:${numNew}|ac)`
            global.snackbarData({message: message})
        }
    }

    private getPermissionDisplayName(role: ISystemRole, system?: ISystem): string {
        return getPermissionDisplayName(role, system) ?? ''
    }

    override dispose() {
        super.dispose();
        this.kosub.unsuscribeAll();
        this.submitRequests.forEach(req => req.abort());
    }
}
